import { manifest as anthropic } from "./aiProviders/anthropic/manifest";
import { manifest as azure } from "./aiProviders/azure/manifest";
import { bedrockGeoPrefix } from "./aiProviders/bedrockAnthropic/geo";
import {
  connectorManifest as awsBedrock,
  manifest as bedrockAnthropic,
} from "./aiProviders/bedrockAnthropic/manifest";
import { manifest as googleGemini } from "./aiProviders/googleGemini/manifest";
import { indexAIProvidersById } from "./aiProviders/manifest";
import { manifest as microsoftFoundry } from "./aiProviders/microsoftFoundry/manifest";
import { manifest as openAI } from "./aiProviders/openai/manifest";
import { openAICompatibleConnectorProviders } from "./aiProviders/openaiCompatible/connectorProviders";
import { manifest as openAICompatible } from "./aiProviders/openaiCompatible/manifest";
import { manifest as oracleOci } from "./aiProviders/oracleOci/manifest";

export {
  availableEffortLevels,
  effortLevels,
  type EffortLevel,
  isEffortLevel,
} from "./aiProviders/effort";

// The settings UI offers providers in this order, catch-alls last.
const aiProviders = [
  anthropic,
  openAI,
  bedrockAnthropic,
  awsBedrock,
  azure,
  googleGemini,
  microsoftFoundry,
  oracleOci,
  ...openAICompatibleConnectorProviders,
  openAICompatible,
] as const;

export type AIProviderId = (typeof aiProviders)[number]["id"];

export const aiProviderIds = aiProviders.map(({ id }) => id) as AIProviderId[];

const aiProvidersById = indexAIProvidersById(aiProviders);

export const configurableAIProviders = aiProviders.filter(
  (provider) => provider.configurable
);

export const creatableAIProviders = configurableAIProviders.filter(
  (provider) => !provider.retired
);

type ConfigurableAIProviderId = (typeof configurableAIProviders)[number]["id"];

export const configurableAIProviderIds = configurableAIProviders.map(
  ({ id }) => id
) as [ConfigurableAIProviderId, ...ConfigurableAIProviderId[]];

// Sentinel for a per-user model preference that pins the env-managed provider
// rather than any configured `ai_providers` row.
export const MANAGED_AI_PROVIDER_ID = "managed";

const managedBedrockProvider = {
  provider: "bedrock-anthropic",
  chatModels: ["claude-opus-5", "claude-fable-5", "claude-sonnet-5"],
  fastModel: "claude-haiku-4-5-20251001",
} as const;

const managedOpenAIProvider = {
  provider: "openai",
  chatModels: ["gpt-5.6-sol", "gpt-5.6-terra", "gpt-5.6-luna"],
  fastModel: "gpt-5.6-luna",
} as const;

const managedAIProviderCatalog = [
  managedBedrockProvider,
  managedOpenAIProvider,
] as const;

export const defaultManagedAIProvider = managedBedrockProvider;
export const defaultManagedChatModel = defaultManagedAIProvider.chatModels[0];
export type ManagedChatModel =
  (typeof managedAIProviderCatalog)[number]["chatModels"][number];
export const managedChatModels: readonly ManagedChatModel[] =
  managedAIProviderCatalog.flatMap(({ chatModels }) => chatModels);
export const managedModelIds = [
  ...new Set(
    managedAIProviderCatalog.flatMap(({ chatModels, fastModel }) => [
      ...chatModels,
      fastModel,
    ])
  ),
];

type AIProviderEnvironment = {
  anthropicBedrockRegion?: string | null;
  openAIApiKeyConfigured?: boolean | null;
};

export function getAIProvider(provider: AIProviderId) {
  const definition = aiProvidersById[provider];
  if (!definition) {
    throw new Error(`Unsupported AI provider: ${provider}`);
  }
  return definition;
}

function isAIProviderId(value: string): value is AIProviderId {
  return Object.hasOwn(aiProvidersById, value);
}

// `microsoft-foundry` replaced a pair of ids split by API surface. Production
// held no rows on either when they were merged, so this only covers rows a
// stale client writes before the migration that renames them lands.
const renamedAIProviderIds: Record<string, AIProviderId> = {
  "microsoft-foundry-claude": microsoftFoundry.id,
  "microsoft-foundry-openai": microsoftFoundry.id,
};

export function resolveAIProviderId(config?: {
  provider?: string | null;
  baseUrl?: string | null;
}): AIProviderId {
  if (config?.provider) {
    if (isAIProviderId(config.provider)) return config.provider;
    const renamed = renamedAIProviderIds[config.provider];
    if (renamed) return renamed;
  }
  if (config?.baseUrl) {
    return openAICompatible.id;
  }
  return anthropic.id;
}

export function resolveManagedAIProviderId(
  environment: AIProviderEnvironment = {}
): AIProviderId | null {
  return environment.anthropicBedrockRegion ? bedrockAnthropic.id : null;
}

export function availableManagedChatModels(
  environment: AIProviderEnvironment = {}
): readonly ManagedChatModel[] {
  const region = environment.anthropicBedrockRegion;
  if (!region) return [];
  // Fable exists only as us. and global. inference profiles, and we don't
  // route through the global profile yet, so US-geo stacks only.
  const usGeo = bedrockGeoPrefix(region) === "us";
  const models: ManagedChatModel[] = defaultManagedAIProvider.chatModels.filter(
    (model) => usGeo || model !== "claude-fable-5"
  );
  if (environment.openAIApiKeyConfigured) {
    models.push(...managedOpenAIProvider.chatModels);
  }
  return models;
}

export function isManagedChatModel(value: string): value is ManagedChatModel {
  return managedChatModels.some((model) => model === value);
}

// A connector-backed provider's endpoint is read from the connector live, so
// editing the connector's URL moves the provider with it; the stale copy in
// `baseUrl` on rows saved before that is deliberately ignored.
export function aiProviderBaseUrl(
  config: { provider?: string | null; baseUrl?: string | null },
  configuration: Record<string, string> | null | undefined
): string | null {
  const definition = getAIProvider(resolveAIProviderId(config));
  if (!definition.endpointFromConnector) return config.baseUrl?.trim() || null;
  if (!configuration) return null;
  const url = definition.connectorBaseUrl
    ? definition.connectorBaseUrl(configuration)
    : configuration.baseUrl;
  return url?.trim() || null;
}

export function aiProviderEndpointUrl(config: {
  provider?: string | null;
  baseUrl?: string | null;
  configuration?: Record<string, string> | null;
}): string | null {
  const definition = getAIProvider(resolveAIProviderId(config));
  return (
    aiProviderBaseUrl(config, config.configuration) ||
    definition.defaultBaseUrl ||
    null
  );
}

type AIProviderCredentials = {
  provider?: string | null;
  baseUrl?: string | null;
  connectedAppId?: string | null;
};

// Every sensitive credential now comes from the backing connector, so binding
// one satisfies each of them.
export function hasRequiredAIProviderCredentials(
  config: AIProviderCredentials
) {
  const definition = getAIProvider(resolveAIProviderId(config));
  const unpacked = definition.unpackCredentials({
    baseUrl: config.baseUrl ?? "",
    apiKey: "",
  });

  return definition.credentialFields.every((field) => {
    if (!field.required) return true;
    if (field.sensitive) return Boolean(config.connectedAppId);
    return Boolean(unpacked[field.key]?.trim());
  });
}

export function defaultChatModel(provider: AIProviderId) {
  return getAIProvider(provider).defaultChatModel;
}

function managedAIProviderModel(preference?: string | null) {
  for (const definition of managedAIProviderCatalog) {
    const chatModel = definition.chatModels.find(
      (model) => model === preference
    );
    if (chatModel) return { definition, chatModel };
  }
  return {
    definition: defaultManagedAIProvider,
    chatModel: defaultManagedChatModel,
  };
}

export function managedAIProviderIdForModel(
  preference?: string | null
): AIProviderId {
  return managedAIProviderModel(preference).definition.provider;
}

export function resolveManagedModel(preference?: string | null) {
  const { definition, chatModel } = managedAIProviderModel(preference);
  return {
    provider: definition.provider,
    chatModel,
    fastModel: definition.fastModel,
  };
}

export function resolveManagedChatModel(preference?: string | null) {
  return managedAIProviderModel(preference).chatModel;
}

export function defaultFastModel(provider: AIProviderId) {
  return getAIProvider(provider).defaultFastModel;
}

export function shouldValidateAIProviderBaseUrl(
  provider: ConfigurableAIProviderId
) {
  return !getAIProvider(provider).skipsBaseUrlValidation;
}
